import React, { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import Header from '../Header';
import {
    Store, Sparkles, Package, Users, ThumbsUp, MessageSquare,
    RotateCcw, FileDown, TrendingUp, LayoutDashboard, CalendarRange,
    AlertCircle, Loader2, ClipboardX, Search, ChevronLeft, ChevronRight,
    Calendar, Filter, ChevronDown, ArrowUpRight, ArrowDownRight,
    X, Check, MapPin, ThumbsDown
} from 'lucide-react';

// ─── Config ───────────────────────────────────────────────
const API_URL = 'https://tpapi.abisaio.com:3060/api/v1/reportsurveyresults?surveyId=1&AreaId=ALL&BranchId=ALL';

const RATING_ORDER = ['Excellent', 'Good', 'Average', 'POOR', 'Very Poor'];
const RECOMMEND_ORDER = ['Definitely Yes', 'Probably Yes', 'Probably not', 'Definitely not'];

const RATING_COLORS = {
    'Excellent': { bg: '#f0fdf4', border: '#bcf2d1', text: '#166534', bar: '#22c55e' },
    'Good': { bg: '#eff6ff', border: '#bfdbfe', text: '#1e40af', bar: '#3b82f6' },
    'Average': { bg: '#fffbeb', border: '#fef3c7', text: '#92400e', bar: '#f59e0b' },
    'POOR': { bg: '#fff1f2', border: '#fecdd3', text: '#9f1239', bar: '#f43f5e' },
    'Very Poor': { bg: '#fef2f2', border: '#fee2e2', text: '#b91c1c', bar: '#ef4444' },
    'Definitely Yes': { bg: '#f0fdf4', border: '#bcf2d1', text: '#166534', bar: '#22c55e' },
    'Probably Yes': { bg: '#eff6ff', border: '#bfdbfe', text: '#1e40af', bar: '#3b82f6' },
    'Probably not': { bg: '#fffbeb', border: '#fef3c7', text: '#92400e', bar: '#f59e0b' },
    'Definitely not': { bg: '#fff1f2', border: '#fecdd3', text: '#9f1239', bar: '#f43f5e' },
};

const CATEGORIES = [
    { key: 'AMBIENCE', label: 'Ambience', icon: <Store size={18} /> },
    { key: 'CLEANLY', label: 'Cleanliness', icon: <Sparkles size={18} /> },
    { key: 'AVAILABLE', label: 'Availability', icon: <Package size={18} /> },
    { key: 'STAFF', label: 'Staff', icon: <Users size={18} /> },
    { key: 'RECOMMEND', label: 'Recommend', icon: <ThumbsUp size={18} /> },
];

// NPS Score: (Promoters - Detractors) / Total Response * 100
// Promoters: 5 & 4 stars (Definitely Yes / Probably Yes / Excellent / Good)
// Detractors: 2 & 1 stars (Probably not / Definitely not / POOR / Very Poor)
const calcNPS = (data, cat = 'RECOMMEND') => {
    if (!data.length) return 0;
    const promoters = data.filter(r => r[cat] === 'Excellent' || r[cat] === 'Good' || r[cat] === 'Definitely Yes' || r[cat] === 'Probably Yes').length;
    const detractors = data.filter(r => r[cat] === 'Probably not' || r[cat] === 'Definitely not' || r[cat] === 'POOR' || r[cat] === 'Very Poor').length;
    return Math.round(((promoters - detractors) / data.length) * 100);
};

const getNPSColor = (score) => {
    if (score >= 50) return { text: '#166534', bg: '#f0fdf4', border: '#bcf2d1', label: 'Excellent' };
    if (score >= 0) return { text: '#1e40af', bg: '#eff6ff', border: '#bfdbfe', label: 'Good' };
    return { text: '#991b1b', bg: '#fef2f2', border: '#fecdd3', label: 'Needs Work' };
};

// ─── Date Helpers ─────────────────────────────────────────
const toYMD = (d) => d.toISOString().slice(0, 10);

const getPeriodDates = (period) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const end = new Date(today); end.setHours(23, 59, 59, 999);

    if (period === 'today') {
        return { from: toYMD(today), to: toYMD(today) };
    }
    if (period === 'weekly') {
        const from = new Date(today);
        from.setDate(today.getDate() - 6);
        return { from: toYMD(from), to: toYMD(today) };
    }
    if (period === 'monthly') {
        const from = new Date(today);
        from.setDate(today.getDate() - 29);
        return { from: toYMD(from), to: toYMD(today) };
    }
    if (period === 'yearly') {
        const from = new Date(today);
        from.setFullYear(today.getFullYear() - 1);
        return { from: toYMD(from), to: toYMD(today) };
    }
    return { from: '', to: '' };
};

const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const formatDateTime = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()} `
        + `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

// ─── Rating Badge ─────────────────────────────────────────
const RatingBadge = ({ value }) => {
    const cfg = RATING_COLORS[value] || { bg: '#f8fafc', text: '#475569', border: '#e2e8f0' };
    return (
        <span style={{
            display: 'inline-block', padding: '2px 8px', borderRadius: '4px',
            fontSize: '10px', fontWeight: '800', backgroundColor: cfg.bg, color: cfg.text,
            border: `1px solid ${cfg.border || 'transparent'}`,
            letterSpacing: '0.4px', whiteSpace: 'nowrap', textTransform: 'uppercase'
        }}>
            {value || '-'}
        </span>
    );
};

const NPSScoreBadge = ({ score }) => {
    if (score === null || score === undefined) {
        return <span style={{ color: '#94a3b8', fontWeight: '500' }}>-</span>;
    }
    const color = getNPSColor(score);
    return (
        <span style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: '800',
            backgroundColor: color.bg,
            color: color.text,
            border: `1px solid ${color.border}`,
            textAlign: 'center',
            minWidth: '38px'
        }}>
            {score}
        </span>
    );
};

// ─── NPS Gauge Arc ────────────────────────────────────────
const NPSGauge = ({ score }) => {
    const npsColor = getNPSColor(score);
    const clamp = Math.max(-100, Math.min(100, score));
    const pct = (clamp + 100) / 200;
    const radius = 38, cx = 60, cy = 46;
    const circumference = Math.PI * radius;
    const dashOffset = circumference * (1 - pct);
    const scoreColor = score >= 50 ? '#10b981' : score >= 0 ? '#3b82f6' : '#f43f5e';
    const TrendIcon = score >= 50 ? ArrowUpRight : score >= 0 ? TrendingUp : ArrowDownRight;

    return (
        <div style={{ textAlign: 'center', position: 'relative', padding: '4px 0 0 0' }}>
            <svg width="120" height="54" viewBox="0 0 120 54" style={{ overflow: 'visible' }}>
                <path d={`M ${cx - radius},${cy} A ${radius},${radius} 0 0,1 ${cx + radius},${cy}`}
                    fill="none" stroke="#f1f5f9" strokeWidth="6" strokeLinecap="round" />
                <path d={`M ${cx - radius},${cy} A ${radius},${radius} 0 0,1 ${cx + radius},${cy}`}
                    fill="none" stroke={scoreColor} strokeWidth="6" strokeLinecap="round"
                    strokeDasharray={circumference} strokeDashoffset={dashOffset}
                    style={{ transition: 'stroke-dashoffset 0.8s ease', opacity: 0.9 }}
                />
            </svg>
            <div style={{ position: 'absolute', bottom: '16px', left: 0, right: 0 }}>
                <div style={{ fontSize: '22px', fontWeight: '800', color: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px' }}>
                    {score}
                    <TrendIcon size={14} style={{ color: scoreColor, opacity: 0.8 }} />
                </div>
                <div style={{ fontSize: '8px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.8px', marginTop: '-2px' }}>NPS Score</div>
            </div>
            <div style={{ marginTop: '4px' }}>
                <span style={{
                    display: 'inline-block', padding: '2px 8px', borderRadius: '4px',
                    fontSize: '9px', fontWeight: '800', border: `1px solid ${npsColor.border}`,
                    backgroundColor: npsColor.bg, color: npsColor.text, textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                }}>
                    {npsColor.label}
                </span>
            </div>
        </div>
    );
};

// ─── Rating Breakdown Card ─────────────────────────────────
const RatingBreakdown = ({ data, categoryKey, label, icon }) => {
    const order = categoryKey === 'RECOMMEND' ? RECOMMEND_ORDER : RATING_ORDER;
    const counts = {};
    data.forEach(r => { const v = r[categoryKey] || 'Unknown'; counts[v] = (counts[v] || 0) + 1; });
    const total = data.length || 1;
    const topRating = order.find(r => counts[r] > 0) || order[0];
    const topPct = Math.round(((counts[topRating] || 0) / total) * 100);

    return (
        <div style={{
            background: '#fff', borderRadius: '12px', padding: '14px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9',
            transition: 'transform 0.2s, box-shadow 0.2s',
            minWidth: '220px', flex: '1 0 auto'
        }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.1)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.06)'; }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                <div style={{ color: '#475569', background: '#f8fafc', padding: '4px', borderRadius: '6px' }}>{icon}</div>
                <div>
                    <div style={{ fontSize: '12px', fontWeight: '800', color: '#1e293b' }}>{label}</div>
                    <div style={{ fontSize: '9px', color: '#94a3b8' }}>{data.length} responses</div>
                </div>
                <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                    <div style={{ fontSize: '16px', fontWeight: '800', color: '#1e293b' }}>{topPct}%</div>
                    <div style={{ fontSize: '8px', color: '#94a3b8' }}>top rating</div>
                </div>
            </div>
            {order.map(rating => {
                const count = counts[rating] || 0;
                const pct = Math.round((count / total) * 100);
                const cfg = RATING_COLORS[rating] || { bar: '#9ca3af', bg: '#f3f4f6', text: '#374151' };
                return (
                    <div key={rating} style={{ marginBottom: '5px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', marginBottom: '2px' }}>
                            <span style={{ color: '#475569', fontWeight: '600' }}>{rating}</span>
                            <span style={{ color: '#94a3b8', fontWeight: '500' }}>{count} <span style={{ color: cfg.text, fontWeight: '700' }}>({pct}%)</span></span>
                        </div>
                        <div style={{ height: '4px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{
                                height: '100%', width: `${pct}%`, background: cfg.bar,
                                borderRadius: '4px', transition: 'width 0.6s ease'
                            }} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// ─── Period Tab Bar ────────────────────────────────────────
const TABS = [
    { id: 'today', label: 'Today' },
    { id: 'weekly', label: 'Weekly' },
    { id: 'monthly', label: 'Monthly' },
    { id: 'yearly', label: 'Yearly' },
    { id: 'custom', label: 'Custom' },
];

// ─── Main Dashboard ───────────────────────────────────────
const FeedbackDashboard = () => {
    const [allData, setAllData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [filterCity, setFilterCity] = useState('ALL');
    const [filterBranch, setFilterBranch] = useState('ALL');
    const [stagedFilters, setStagedFilters] = useState({ city: 'ALL', branch: 'ALL' });
    const [showFilterPopover, setShowFilterPopover] = useState(false);
    const [showDatePopover, setShowDatePopover] = useState(false);
    const [activePeriod, setActivePeriod] = useState('monthly');
    const [customFrom, setCustomFrom] = useState('');
    const [customTo, setCustomTo] = useState('');
    const [activeView, setActiveView] = useState('dashboard');
    const [comparisonSearch, setComparisonSearch] = useState('');
    const [selectedDrillDownStore, setSelectedDrillDownStore] = useState('');
    const PAGE_SIZE = 15;

    // Compute active date range
    const dateRange = useMemo(() => {
        if (activePeriod === 'custom') return { from: customFrom, to: customTo };
        return getPeriodDates(activePeriod);
    }, [activePeriod, customFrom, customTo]);

    // Fetch
    const fetchData = async () => {
        setLoading(true); setError('');
        try {
            const token = localStorage.getItem('AuthorizationToken');
            const res = await fetch(API_URL, {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            });
            if (res.status === 401 || res.status === 403) {
                window.alert('⚠️ Session expired. Please login again.');
                setError('Unauthorized. Please login again.');
                return;
            }
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const json = await res.json();
            setAllData(Array.isArray(json) ? json : []);
        } catch (e) {
            setError(`Failed to load data: ${e.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    // Filter options (UI City maps to AreaName)
    const cities = useMemo(() => ['ALL', ...new Set(allData.map(r => r.AreaName).filter(Boolean))].sort(), [allData]);
    const branches = useMemo(() => ['ALL', ...new Set(allData.map(r => r.BranchName).filter(Boolean))].sort(), [allData]);

    // Filtered by date only (used for overall comparisons)
    const dateOnlyFiltered = useMemo(() => {
        return allData.filter(r => {
            const rowDate = r.BusinessDate ? r.BusinessDate.slice(0, 10) : '';
            if (dateRange.from && rowDate < dateRange.from) return false;
            if (dateRange.to && rowDate > dateRange.to) return false;
            return true;
        });
    }, [allData, dateRange]);

    // Date-filtered and region-filtered data (UI City filters on AreaName)
    const dateFiltered = useMemo(() => {
        return dateOnlyFiltered.filter(r => {
            if (filterCity !== 'ALL' && r.AreaName !== filterCity) return false;
            if (filterBranch !== 'ALL' && r.BranchName !== filterBranch) return false;
            return true;
        });
    }, [dateOnlyFiltered, filterCity, filterBranch]);

    // Search-filtered data
    const filtered = useMemo(() => {
        if (!search) return dateFiltered;
        const q = search.toLowerCase();
        return dateFiltered.filter(r =>
            (r.UserName || '').toLowerCase().includes(q) ||
            (r.RefDocID || '').toLowerCase().includes(q) ||
            (r.BillNo || '').toLowerCase().includes(q) ||
            (r.Mobile || '').toLowerCase().includes(q) ||
            (r.SaleItems || '').toLowerCase().includes(q)
        );
    }, [dateFiltered, search]);

    // KPIs
    const totalResp = filtered.length;
    const npsScore = calcNPS(filtered);
    const getCatNPS = (cat) => calcNPS(filtered, cat);

    // Cumulative score of each city - citywise NPS score (maps to AreaName)
    const citywiseNPS = useMemo(() => {
        const cityGroups = {};
        dateOnlyFiltered.forEach(r => {
            const city = r.AreaName || 'Unknown';
            if (!cityGroups[city]) {
                cityGroups[city] = [];
            }
            cityGroups[city].push(r);
        });

        return Object.entries(cityGroups).map(([city, responses]) => {
            return {
                city,
                nps: calcNPS(responses),
                total: responses.length
            };
        }).sort((a, b) => b.nps - a.nps || b.total - a.total);
    }, [dateOnlyFiltered]);

    // Top 10 and Bottom 10 stores (branches)
    const storeNPSList = useMemo(() => {
        const storeGroups = {};
        dateOnlyFiltered.forEach(r => {
            const store = r.BranchName || 'Unknown';
            if (!storeGroups[store]) {
                storeGroups[store] = [];
            }
            storeGroups[store].push(r);
        });

        return Object.entries(storeGroups).map(([store, responses]) => {
            return {
                store,
                city: responses[0]?.AreaName || 'Unknown',
                nps: calcNPS(responses),
                total: responses.length
            };
        });
    }, [dateOnlyFiltered]);

    const top10Stores = useMemo(() => {
        return [...storeNPSList]
            .sort((a, b) => b.nps - a.nps || b.total - a.total)
            .slice(0, 10);
    }, [storeNPSList]);

    const bottom10Stores = useMemo(() => {
        return [...storeNPSList]
            .sort((a, b) => a.nps - b.nps || a.total - b.total)
            .slice(0, 10);
    }, [storeNPSList]);

    // Month-on-Month (MoM) NPS score of each store
    const momNPSData = useMemo(() => {
        const branchMonthGroups = {};
        const uniqueMonthsSet = new Set();

        dateOnlyFiltered.forEach(r => {
            const branch = r.BranchName || 'Unknown';
            const dateStr = r.BusinessDate ? r.BusinessDate.slice(0, 10) : '';
            if (!dateStr) return;
            
            const dateObj = new Date(dateStr);
            if (isNaN(dateObj.getTime())) return;

            const sortKey = dateStr.slice(0, 7); // YYYY-MM
            const label = dateObj.toLocaleString('default', { month: 'short', year: '2-digit' });

            uniqueMonthsSet.add(JSON.stringify({ sortKey, label }));

            if (!branchMonthGroups[branch]) {
                branchMonthGroups[branch] = {};
            }
            if (!branchMonthGroups[branch][sortKey]) {
                branchMonthGroups[branch][sortKey] = [];
            }
            branchMonthGroups[branch][sortKey].push(r);
        });

        const sortedMonths = Array.from(uniqueMonthsSet)
            .map(m => JSON.parse(m))
            .sort((a, b) => a.sortKey.localeCompare(b.sortKey));

        const branchesMoM = Object.entries(branchMonthGroups).map(([branch, monthsData]) => {
            const momScores = {};
            let city = 'Unknown';
            sortedMonths.forEach(m => {
                const responses = monthsData[m.sortKey] || [];
                if (responses.length > 0) {
                    city = responses[0].AreaName || 'Unknown';
                }
                const hasData = responses.length > 0;
                momScores[m.sortKey] = {
                    nps: hasData ? calcNPS(responses) : null,
                    total: responses.length
                };
            });

            return {
                branch,
                city,
                momScores
            };
        }).sort((a, b) => a.branch.localeCompare(b.branch));

        return {
            months: sortedMonths,
            branches: branchesMoM
        };
    }, [dateOnlyFiltered]);

    const momCityNPSData = useMemo(() => {
        const cityMonthGroups = {};
        const uniqueMonthsSet = new Set();

        dateOnlyFiltered.forEach(r => {
            const city = r.AreaName || 'Unknown';
            const dateStr = r.BusinessDate ? r.BusinessDate.slice(0, 10) : '';
            if (!dateStr) return;
            
            const dateObj = new Date(dateStr);
            if (isNaN(dateObj.getTime())) return;

            const sortKey = dateStr.slice(0, 7); // YYYY-MM
            const label = dateObj.toLocaleString('default', { month: 'short', year: '2-digit' });

            uniqueMonthsSet.add(JSON.stringify({ sortKey, label }));

            if (!cityMonthGroups[city]) {
                cityMonthGroups[city] = {};
            }
            if (!cityMonthGroups[city][sortKey]) {
                cityMonthGroups[city][sortKey] = [];
            }
            cityMonthGroups[city][sortKey].push(r);
        });

        const sortedMonths = Array.from(uniqueMonthsSet)
            .map(m => JSON.parse(m))
            .sort((a, b) => a.sortKey.localeCompare(b.sortKey));

        const citiesMoM = Object.entries(cityMonthGroups).map(([city, monthsData]) => {
            const momScores = {};
            sortedMonths.forEach(m => {
                const responses = monthsData[m.sortKey] || [];
                const hasData = responses.length > 0;
                momScores[m.sortKey] = {
                    nps: hasData ? calcNPS(responses) : null,
                    total: responses.length
                };
            });

            return {
                city,
                momScores
            };
        }).sort((a, b) => a.city.localeCompare(b.city));

        return {
            months: sortedMonths,
            cities: citiesMoM
        };
    }, [dateOnlyFiltered]);

    // Detractors hotspot diagnosis memo
    const detractorInsights = useMemo(() => {
        const isOverallDetractor = (r) => {
            const rec = r.RECOMMEND;
            return rec === 'Probably not' || rec === 'Definitely not' || rec === 'POOR' || rec === 'Very Poor';
        };

        const isCategoryDetractor = (val) => {
            return val === 'Poor' || val === 'POOR' || val === 'Very Poor' || val === 'Probably not' || val === 'Definitely not';
        };

        // 1. City wise detractors
        const cityDetractors = {};
        const cityTotals = {};
        dateOnlyFiltered.forEach(r => {
            const city = r.AreaName || 'Unknown';
            cityTotals[city] = (cityTotals[city] || 0) + 1;
            if (isOverallDetractor(r)) {
                cityDetractors[city] = (cityDetractors[city] || 0) + 1;
            }
        });

        let worstCity = { name: 'None', count: 0, rate: 0, total: 0 };
        Object.entries(cityDetractors).forEach(([name, count]) => {
            const total = cityTotals[name] || 1;
            const rate = Math.round((count / total) * 100);
            if (count > worstCity.count || (count === worstCity.count && rate > worstCity.rate)) {
                worstCity = { name, count, rate, total };
            }
        });

        // 2. Shop wise detractors
        const shopDetractors = {};
        const shopTotals = {};
        const shopCities = {};
        dateOnlyFiltered.forEach(r => {
            const shop = r.BranchName || 'Unknown';
            shopTotals[shop] = (shopTotals[shop] || 0) + 1;
            shopCities[shop] = r.AreaName || 'Unknown';
            if (isOverallDetractor(r)) {
                shopDetractors[shop] = (shopDetractors[shop] || 0) + 1;
            }
        });

        let worstShop = { name: 'None', city: 'None', count: 0, rate: 0, total: 0 };
        Object.entries(shopDetractors).forEach(([name, count]) => {
            const total = shopTotals[name] || 1;
            const rate = Math.round((count / total) * 100);
            if (count > worstShop.count || (count === worstShop.count && rate > worstShop.rate)) {
                worstShop = { name, city: shopCities[name], count, rate, total };
            }
        });

        // 3. Question wise detractors
        const categoriesList = [
            { key: 'AMBIENCE', label: 'Ambience' },
            { key: 'CLEANLY', label: 'Cleanliness' },
            { key: 'AVAILABLE', label: 'Availability' },
            { key: 'STAFF', label: 'Staff Service' },
            { key: 'RECOMMEND', label: 'NPS Recommend' }
        ];

        const categoryCounts = categoriesList.map(cat => {
            let count = 0;
            let total = 0;
            dateOnlyFiltered.forEach(r => {
                const val = r[cat.key];
                if (val && val !== '-') {
                    total++;
                    if (isCategoryDetractor(val)) {
                        count++;
                    }
                }
            });
            const rate = total > 0 ? Math.round((count / total) * 100) : 0;
            return {
                label: cat.label,
                count,
                rate,
                total
            };
        }).sort((a, b) => b.count - a.count || b.rate - a.rate);

        const worstQuestion = categoryCounts[0] || { label: 'None', count: 0, rate: 0, total: 0 };

        return {
            worstCity,
            worstShop,
            worstQuestion,
            allQuestions: categoryCounts
        };
    }, [dateOnlyFiltered]);

    // List of all individual detractors
    const detractorList = useMemo(() => {
        return dateOnlyFiltered.map(r => {
            const detractedQuestions = [];
            const isDetractorVal = (v) => v === 'Poor' || v === 'POOR' || v === 'Very Poor';
            const isRecDetractorVal = (v) => v === 'Probably not' || v === 'Definitely not' || v === 'POOR' || v === 'Very Poor';

            if (isDetractorVal(r.AMBIENCE)) detractedQuestions.push('Ambience');
            if (isDetractorVal(r.CLEANLY)) detractedQuestions.push('Cleanliness');
            if (isDetractorVal(r.AVAILABLE)) detractedQuestions.push('Availability');
            if (isDetractorVal(r.STAFF)) detractedQuestions.push('Staff Service');
            if (isRecDetractorVal(r.RECOMMEND)) detractedQuestions.push('Recommend');

            return {
                ...r,
                detractedQuestions
            };
        }).filter(item => item.detractedQuestions.length > 0);
    }, [dateOnlyFiltered]);

    // CSV exporter for detractor records
    const handleDownloadCSV = () => {
        if (detractorList.length === 0) {
            window.alert('No detractor records to download.');
            return;
        }

        const headers = ['Bill No', 'Customer Name', 'Customer Number', 'Shop', 'City', 'Item Name', 'Detracted Categories'];
        const csvRows = [headers.join(',')];

        detractorList.forEach((item) => {
            const billNo = `"${(item.BillNo || '').replace(/"/g, '""')}"`;
            const name = `"${(item.UserName || '').replace(/"/g, '""')}"`;
            const mobile = `"${(item.Mobile || '').replace(/"/g, '""')}"`;
            const shop = `"${(item.BranchName || '').replace(/"/g, '""')}"`;
            const city = `"${(item.AreaName || '').replace(/"/g, '""')}"`;
            const items = `"${(item.SaleItems || '').replace(/"/g, '""')}"`;
            const cats = `"${item.detractedQuestions.join(', ')}"`;

            csvRows.push([billNo, name, mobile, shop, city, items, cats].join(','));
        });

        const csvContent = '\uFEFF' + csvRows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `detractors_report_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const filteredBranchesMoM = useMemo(() => {
        if (!comparisonSearch) return momNPSData.branches;
        const q = comparisonSearch.toLowerCase();
        return momNPSData.branches.filter(b => b.branch.toLowerCase().includes(q));
    }, [momNPSData, comparisonSearch]);

    // Detailed Month-on-Month calculations for the selected drill-down store
    const selectedStoreDrillDownData = useMemo(() => {
        if (!selectedDrillDownStore) return [];

        const storeResponses = dateOnlyFiltered.filter(r => (r.BranchName || 'Unknown') === selectedDrillDownStore);

        const monthGroups = {};
        storeResponses.forEach(r => {
            const dateStr = r.BusinessDate ? r.BusinessDate.slice(0, 10) : '';
            if (!dateStr) return;
            const sortKey = dateStr.slice(0, 7); // YYYY-MM
            if (!monthGroups[sortKey]) {
                monthGroups[sortKey] = [];
            }
            monthGroups[sortKey].push(r);
        });

        const sortedMonths = Object.keys(monthGroups).sort();

        const monthsData = sortedMonths.map((sortKey) => {
            const responses = monthGroups[sortKey];
            
            let promoters = 0;
            let passives = 0;
            let detractors = 0;
            
            let ambienceSum = 0, ambienceCount = 0;
            let cleanlinessSum = 0, cleanlinessCount = 0;
            let availabilitySum = 0, availabilityCount = 0;
            let staffSum = 0, staffCount = 0;
            let recommendSum = 0, recommendCount = 0;

            const getRatingValue = (val) => {
                if (!val) return null;
                if (val === 'Excellent') return 5;
                if (val === 'Good') return 4;
                if (val === 'Average') return 3;
                if (val === 'Poor' || val === 'POOR') return 2;
                if (val === 'Very Poor') return 1;
                
                const parsed = parseFloat(val);
                if (!isNaN(parsed)) return parsed;
                return null;
            };

            responses.forEach(r => {
                const rec = r.RECOMMEND;
                const isPromoter = rec === 'Definitely Yes' || rec === 'Probably Yes' || rec === 'Excellent' || rec === 'Good';
                const isDetractor = rec === 'Probably not' || rec === 'Definitely not' || rec === 'POOR' || rec === 'Very Poor';
                
                if (isPromoter) promoters++;
                else if (isDetractor) detractors++;
                else passives++;

                const ambVal = getRatingValue(r.AMBIENCE);
                if (ambVal !== null) { ambienceSum += ambVal; ambienceCount++; }

                const cleanVal = getRatingValue(r.CLEANLY);
                if (cleanVal !== null) { cleanlinessSum += cleanVal; cleanlinessCount++; }

                const availVal = getRatingValue(r.AVAILABLE);
                if (availVal !== null) { availabilitySum += availVal; availabilityCount++; }

                const staffVal = getRatingValue(r.STAFF);
                if (staffVal !== null) { staffSum += staffVal; staffCount++; }

                let recNumVal = null;
                if (rec === 'Definitely Yes' || rec === 'Excellent') recNumVal = 5;
                else if (rec === 'Probably Yes' || rec === 'Good') recNumVal = 4;
                else if (rec === 'Neutral' || rec === 'Average') recNumVal = 3;
                else if (rec === 'Probably not' || rec === 'POOR') recNumVal = 2;
                else if (rec === 'Definitely not' || rec === 'Very Poor') recNumVal = 1;
                else {
                    const parsed = parseFloat(rec);
                    if (!isNaN(parsed)) recNumVal = parsed;
                }
                if (recNumVal !== null) { recommendSum += recNumVal; recommendCount++; }
            });

            const total = responses.length;
            const nps = total > 0 ? Math.round(((promoters - detractors) / total) * 100) : 0;

            const dateObj = new Date(sortKey + '-02');
            const monthLabel = dateObj.toLocaleString('default', { month: 'long', year: 'numeric' });

            return {
                sortKey,
                monthLabel,
                nps,
                total,
                promoters,
                passives,
                detractors,
                ratings: {
                    ambience: ambienceCount > 0 ? (ambienceSum / ambienceCount).toFixed(1) : '-',
                    cleanliness: cleanlinessCount > 0 ? (cleanlinessSum / cleanlinessCount).toFixed(1) : '-',
                    availability: availabilityCount > 0 ? (availabilitySum / availabilityCount).toFixed(1) : '-',
                    staff: staffCount > 0 ? (staffSum / staffCount).toFixed(1) : '-',
                    recommend: recommendCount > 0 ? (recommendSum / recommendCount).toFixed(1) : '-',
                }
            };
        });

        // Compute MoM Change
        return monthsData.map((curr, idx) => {
            let changeText = 'First Month';
            let changeType = 'none';
            let changeValue = 0;

            if (idx > 0) {
                const prev = monthsData[idx - 1];
                changeValue = curr.nps - prev.nps;
                if (changeValue > 0) {
                    changeText = `↑ +${changeValue} Improvement`;
                    changeType = 'improvement';
                } else if (changeValue < 0) {
                    changeText = `↓ ${changeValue} Decline`;
                    changeType = 'decline';
                } else {
                    changeText = `→ No Change`;
                    changeType = 'none';
                }
            }

            return {
                ...curr,
                changeText,
                changeType,
                changeValue
            };
        }).reverse();
    }, [selectedDrillDownStore, dateOnlyFiltered]);

    // Pre-select first store on load
    useEffect(() => {
        if (momNPSData.branches.length > 0 && !selectedDrillDownStore) {
            const firstStore = top10Stores[0]?.store || momNPSData.branches[0]?.branch || '';
            setSelectedDrillDownStore(firstStore);
        }
    }, [momNPSData, top10Stores, selectedDrillDownStore]);

    // Pagination
    useEffect(() => setPage(1), [activePeriod, customFrom, customTo, search, filterCity, filterBranch]);
    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    // Excel Export
    const exportToExcel = () => {
        if (!filtered.length) return;
        const wb = XLSX.utils.book_new();
        const headers = ['Unique ID', 'Bill No', 'Ref Doc ID', 'Business Date', 'Created Date', 'City', 'Branch', 'User Name', 'Mobile', 'Sale Items', 'Ambience', 'Cleanliness', 'Availability', 'Staff', 'Recommend'];
        const rows = filtered.map(r => [
            r.UniqueID || '-', r.BillNo || '-', r.RefDocID || '-', formatDate(r.BusinessDate), formatDateTime(r.CreatedDate),
            r.AreaName || '-', r.BranchName || '-', r.UserName || '-', r.Mobile || '-', r.SaleItems || '-',
            r.AMBIENCE || '-', r.CLEANLY || '-', r.AVAILABLE || '-', r.STAFF || '-', r.RECOMMEND || '-',
        ]);
        const ws1 = XLSX.utils.aoa_to_sheet([headers, ...rows]);
        ws1['!cols'] = [{ wch: 12 }, { wch: 14 }, { wch: 18 }, { wch: 14 }, { wch: 18 }, { wch: 18 }, { wch: 22 }, { wch: 20 }, { wch: 13 }, { wch: 30 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 16 }];
        XLSX.utils.book_append_sheet(wb, ws1, 'Responses');

        const branchSummary = {};
        filtered.forEach(r => {
            const b = r.BranchName || 'Unknown';
            if (!branchSummary[b]) branchSummary[b] = { city: r.AreaName || '-', total: 0, excellent: 0, recommend: 0, promoters: 0, detractors: 0 };
            branchSummary[b].total++;
            if (r.AMBIENCE === 'Excellent' && r.CLEANLY === 'Excellent' && r.AVAILABLE === 'Excellent' && r.STAFF === 'Excellent') branchSummary[b].excellent++;
            if (r.RECOMMEND === 'Definitely Yes' || r.RECOMMEND === 'Probably Yes') branchSummary[b].recommend++;
            if (r.RECOMMEND === 'Definitely Yes' || r.RECOMMEND === 'Probably Yes' || r.RECOMMEND === 'Excellent' || r.RECOMMEND === 'Good') branchSummary[b].promoters++;
            if (r.RECOMMEND === 'Probably not' || r.RECOMMEND === 'Definitely not' || r.RECOMMEND === 'POOR' || r.RECOMMEND === 'Very Poor') branchSummary[b].detractors++;
        });
        const sh = ['City', 'Branch', 'Total Responses', 'All Excellent', 'Excellent %', 'Recommend', 'Recommend %', 'NPS Score'];
        const sr = Object.entries(branchSummary).map(([b, s]) => [
            s.city, b, s.total, s.excellent, `${Math.round(s.excellent / s.total * 100)}%`,
            s.recommend, `${Math.round(s.recommend / s.total * 100)}%`,
            Math.round(((s.promoters - s.detractors) / s.total) * 100),
        ]);
        const ws2 = XLSX.utils.aoa_to_sheet([sh, ...sr]);
        ws2['!cols'] = [{ wch: 18 }, { wch: 24 }, { wch: 18 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 12 }];
        XLSX.utils.book_append_sheet(wb, ws2, 'Branch Summary');
        XLSX.writeFile(wb, `Feedback_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    // ── Period label ──
    const periodLabel = useMemo(() => {
        if (activePeriod === 'custom') {
            if (customFrom && customTo) return `${formatDate(customFrom)} – ${formatDate(customTo)}`;
            return 'Custom range';
        }
        const dr = getPeriodDates(activePeriod);
        return `${formatDate(dr.from)} – ${formatDate(dr.to)}`;
    }, [activePeriod, customFrom, customTo]);

    // ── Styles ──
    const s = {
        page: { minHeight: '100vh', background: '#f0f4f8', fontFamily: "'Segoe UI',Inter,sans-serif" },
        body: { padding: '16px 20px', maxWidth: '1700px', margin: '0 auto' },
        // Header bar
        headerBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' },
        titleWrap: {},
        title: { fontSize: '22px', fontWeight: '700', color: '#0f172a', margin: 0, letterSpacing: '-0.3px' },
        subtitle: { fontSize: '12px', color: '#64748b', marginTop: '2px' },
        // Tab bar / Global Filters
        tabBar: { display: 'flex', alignItems: 'center', background: '#fff', borderRadius: '12px', padding: '12px 18px', boxShadow: '0 4px 15px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', flexWrap: 'wrap', gap: '16px', marginBottom: '20px' },
        tabInner: { display: 'flex', gap: '4px', flexWrap: 'wrap', background: '#f1f5f9', padding: '4px', borderRadius: '10px', border: '1px solid #e2e8f0' },
        globalFilters: { display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' },
        hSelectGroup: { display: 'flex', flexDirection: 'column', gap: '2px' },
        hSelectLabel: { fontSize: '9px', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' },
        hSelect: { padding: '4px 8px', fontSize: '12px', fontWeight: '700', border: '1px solid #e2e8f0', borderRadius: '6px', outline: 'none', background: '#f8fafc', color: '#1e293b', cursor: 'pointer', minWidth: '120px' },
        kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(145px,1fr))', gap: '12px', marginBottom: '20px' },
        kpiCard: { borderRadius: '12px', padding: '12px 14px', boxShadow: '0 2px 10px rgba(0,0,0,0.04)', transition: 'transform 0.2s', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '8px' },
        kpiNum: { fontSize: '22px', fontWeight: '800', lineHeight: 1.1, color: '#1e293b' },
        kpiLbl: { fontSize: '11px', color: '#64748b', fontWeight: '700' },
        // Chart row
        chartRow: { display: 'flex', overflowX: 'auto', gap: '16px', paddingBottom: '12px', marginBottom: '14px', scrollbarWidth: 'none', '-ms-overflow-style': 'none' },
        // Filters
        filters: { background: '#fff', borderRadius: '14px', padding: '14px 16px', marginBottom: '14px', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' },
        label: { fontSize: '11px', fontWeight: '700', color: '#374151', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' },
        input: { padding: '8px 12px', fontSize: '12px', border: '1.5px solid #e2e8f0', borderRadius: '8px', outline: 'none', background: '#f8fafc', color: '#1e293b', transition: 'border-color 0.2s' },
        select: { padding: '8px 12px', fontSize: '12px', border: '1.5px solid #e2e8f0', borderRadius: '8px', outline: 'none', background: '#f8fafc', color: '#1e293b', transition: 'border-color 0.2s', cursor: 'pointer' },
        // Table
        tbl: { background: '#fff', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', marginBottom: '12px' },
        th: { padding: '14px 16px', fontSize: '11px', fontWeight: '700', color: '#475569', textAlign: 'left', background: '#f8fafc', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap', letterSpacing: '0.6px', textTransform: 'uppercase' },
        td: { padding: '12px 16px', fontSize: '12px', color: '#1e293b', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' },
        // Buttons
        btnBlue: { padding: '8px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 1px 2px rgba(37,99,235,0.2)', transition: 'all 0.2s' },
        btnGray: { padding: '8px 16px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontWeight: '600', transition: 'all 0.2s' },
    };

    const KPI_CARDS = [
        { num: totalResp, lbl: 'Total Reps.', color: '#1e3a8a', icon: <MessageSquare size={16} />, bg: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', border: '#bfdbfe' },
        { num: getCatNPS('AMBIENCE'), lbl: 'Ambience', color: '#064e3b', icon: <Store size={16} />, bg: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)', border: '#a7f3d0' },
        { num: getCatNPS('CLEANLY'), lbl: 'Cleanliness', color: '#0c4a6e', icon: <Sparkles size={16} />, bg: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', border: '#bae6fd' },
        { num: getCatNPS('AVAILABLE'), lbl: 'Availability', color: '#78350f', icon: <Package size={16} />, bg: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)', border: '#fde68a' },
        { num: getCatNPS('STAFF'), lbl: 'Staff Score', color: '#312e81', icon: <Users size={16} />, bg: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)', border: '#c7d2fe' },
        { num: getCatNPS('RECOMMEND'), lbl: 'Recommend', color: '#4c1d95', icon: <ThumbsUp size={16} />, bg: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)', border: '#ddd6fe' },
    ];

    return (
        <>
            {/* Inject font */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
                * { box-sizing: border-box; }
                input:focus { border-color: #3b82f6 !important; box-shadow: 0 0 0 3px rgba(59,130,246,0.15); }
                .kpi-card:hover { transform: translateY(-3px); box-shadow: 0 10px 28px rgba(0,0,0,0.10) !important; }
            `}</style>
            <div style={s.page}>
                <Header />
                <div style={s.body}>

                    {/* ── Page Header ── */}
                    <div style={{ ...s.headerBar, marginBottom: '24px' }}>
                        <div style={s.titleWrap}>
                            <h2 style={{ ...s.title, display: 'flex', alignItems: 'center', gap: '12px', fontSize: '24px' }}>
                                <LayoutDashboard size={28} style={{ color: '#3b82f6' }} />
                                Customer Feedback Dashboard
                            </h2>
                            <p style={{ ...s.subtitle, fontSize: '13px', marginLeft: '40px' }}>Net Promoter Score &amp; Survey Analytics · <strong style={{ color: '#3b82f6' }}>{periodLabel}</strong></p>
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button style={{ ...s.btnGray, padding: '10px 20px' }} onClick={fetchData} disabled={loading}>
                                <RotateCcw size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                                {loading ? 'Fetching…' : 'Refresh'}
                            </button>
                            <button style={{ ...s.btnBlue, padding: '10px 20px' }} onClick={exportToExcel} disabled={!filtered.length}>
                                <FileDown size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                                Export Excel
                            </button>
                        </div>
                    </div>

                    {/* ── View Switcher Tabs ── */}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
                        <button 
                            onClick={() => setActiveView('dashboard')}
                            style={{
                                padding: '8px 16px',
                                border: 'none',
                                background: 'none',
                                fontSize: '14px',
                                fontWeight: activeView === 'dashboard' ? '700' : '500',
                                color: activeView === 'dashboard' ? '#2563eb' : '#64748b',
                                borderBottom: activeView === 'dashboard' ? '3px solid #2563eb' : '3px solid transparent',
                                cursor: 'pointer',
                                paddingBottom: '12px',
                                marginBottom: '-11px',
                                transition: 'all 0.2s'
                            }}
                        >
                            Overview Dashboard
                        </button>
                        <button 
                            onClick={() => setActiveView('comparison')}
                            style={{
                                padding: '8px 16px',
                                border: 'none',
                                background: 'none',
                                fontSize: '14px',
                                fontWeight: activeView === 'comparison' ? '700' : '500',
                                color: activeView === 'comparison' ? '#2563eb' : '#64748b',
                                borderBottom: activeView === 'comparison' ? '3px solid #2563eb' : '3px solid transparent',
                                cursor: 'pointer',
                                paddingBottom: '12px',
                                marginBottom: '-11px',
                                transition: 'all 0.2s'
                            }}
                        >
                            Comparison Analytics
                        </button>
                    </div>

                    {/* ── Global Filters & Tabs ── */}
                    <div style={s.tabBar}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
                            <div style={s.tabInner}>
                                {TABS.map(tab => {
                                    const active = activePeriod === tab.id;
                                    return (
                                        <button
                                            key={tab.id}
                                            onClick={() => setActivePeriod(tab.id)}
                                            style={{
                                                padding: '6px 18px',
                                                borderRadius: '6px',
                                                border: 'none',
                                                fontSize: '13px',
                                                fontWeight: active ? '600' : '500',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                background: active ? '#fff' : 'transparent',
                                                color: active ? '#0f172a' : '#64748b',
                                                boxShadow: active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                            }}
                                        >
                                            {tab.label}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Intimate Custom date pickers dropdown next to tabs */}
                            {activePeriod === 'custom' && (
                                <div style={{ position: 'relative', marginLeft: '-4px' }}>
                                    <button
                                        onClick={() => setShowDatePopover(!showDatePopover)}
                                        style={{ ...s.btnGray, padding: '5px 12px', display: 'flex', alignItems: 'center', gap: '6px', background: showDatePopover ? '#eff6ff' : '#fff', borderColor: showDatePopover ? '#3b82f6' : '#e2e8f0', color: showDatePopover ? '#2563eb' : '#475569', borderRadius: '6px' }}
                                    >
                                        <Calendar size={14} />
                                        <span style={{ fontSize: '12px', fontWeight: '700' }}>
                                            {customFrom && customTo ? `${formatDate(customFrom)} - ${formatDate(customTo)}` : 'Select Dates'}
                                        </span>
                                        <ChevronDown size={14} style={{ transform: showDatePopover ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                                    </button>

                                    {showDatePopover && (
                                        <div style={{
                                            position: 'absolute', top: '120%', left: 0,
                                            background: '#fff', borderRadius: '12px', padding: '16px',
                                            boxShadow: '0 15px 40px rgba(0,0,0,0.18)', border: '1px solid #e2e8f0',
                                            zIndex: 100, minWidth: '260px', display: 'flex', flexDirection: 'column', gap: '16px'
                                        }}>
                                            <div style={{ fontSize: '12px', fontWeight: '800', color: '#1e293b', borderBottom: '1px solid #f1f5f9', paddingBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                                                Custom Range
                                                <button onClick={() => setShowDatePopover(false)} style={{ border: 'none', background: 'none', color: '#94a3b8', cursor: 'pointer' }}><X size={16} /></button>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                                <div>
                                                    <label style={{ ...s.label, fontSize: '10px', color: '#64748b', marginBottom: '4px', display: 'block', fontWeight: '700' }}>START DATE</label>
                                                    <input type="date" style={{ ...s.input, width: '100%', padding: '8px 12px' }} value={customFrom} onChange={e => setCustomFrom(e.target.value)} max={customTo || undefined} />
                                                </div>
                                                <div>
                                                    <label style={{ ...s.label, fontSize: '10px', color: '#64748b', marginBottom: '4px', display: 'block', fontWeight: '700' }}>END DATE</label>
                                                    <input type="date" style={{ ...s.input, width: '100%', padding: '8px 12px' }} value={customTo} onChange={e => setCustomTo(e.target.value)} min={customFrom || undefined} />
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => setShowDatePopover(false)}
                                                style={{ ...s.btnBlue, width: '100%', padding: '10px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px' }}
                                            >
                                                <Check size={14} /> Apply Dates
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                            {activeView !== 'comparison' && (
                                <>
                                    <div style={{ width: '1px', height: '24px', background: '#e2e8f0', margin: '0 8px' }}></div>

                                    {/* Filter Popover Button */}
                                    <div style={{ position: 'relative' }}>
                                        <button
                                            onClick={() => setShowFilterPopover(!showFilterPopover)}
                                            style={{
                                                ...s.btnGray,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                padding: '8px 16px',
                                                background: (filterCity !== 'ALL' || filterBranch !== 'ALL') ? '#eff6ff' : '#fff',
                                                borderColor: (filterCity !== 'ALL' || filterBranch !== 'ALL') ? '#3b82f6' : '#e2e8f0',
                                                color: (filterCity !== 'ALL' || filterBranch !== 'ALL') ? '#2563eb' : '#475569'
                                            }}
                                        >
                                            <Filter size={16} />
                                            <span style={{ fontSize: '13px', fontWeight: '700' }}>Region Filters</span>
                                            {(filterCity !== 'ALL' || filterBranch !== 'ALL') && (
                                                <span style={{ background: '#2563eb', color: '#fff', padding: '1px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: '800' }}>!</span>
                                            )}
                                            <ChevronDown size={14} style={{ transform: showFilterPopover ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', marginLeft: '4px' }} />
                                        </button>

                                        {showFilterPopover && (
                                            <div style={{
                                                position: 'absolute', top: '120%', left: 0,
                                                background: '#fff', borderRadius: '12px', padding: '18px',
                                                boxShadow: '0 15px 40px rgba(0,0,0,0.18)', border: '1px solid #e2e8f0',
                                                zIndex: 100, minWidth: '320px', display: 'flex', flexDirection: 'column', gap: '16px'
                                            }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px' }}>
                                                    <span style={{ fontSize: '11px', fontWeight: '800', color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Select Branch & Region</span>
                                                    <button onClick={() => setShowFilterPopover(false)} style={{ border: 'none', background: 'none', color: '#94a3b8', cursor: 'pointer' }}><X size={18} /></button>
                                                </div>
                                                <div style={s.hSelectGroup}>
                                                    <label style={s.hSelectLabel}>City</label>
                                                    <select style={{ ...s.hSelect, padding: '7px 10px' }} value={stagedFilters.city} onChange={e => setStagedFilters({ ...stagedFilters, city: e.target.value })}>
                                                        {cities.map(c => <option key={c} value={c}>{c}</option>)}
                                                    </select>
                                                </div>
                                                <div style={s.hSelectGroup}>
                                                    <label style={s.hSelectLabel}>Branch</label>
                                                    <select style={{ ...s.hSelect, padding: '7px 10px' }} value={stagedFilters.branch} onChange={e => setStagedFilters({ ...stagedFilters, branch: e.target.value })}>
                                                        {branches.map(b => <option key={b} value={b}>{b}</option>)}
                                                    </select>
                                                </div>
                                                <div style={{ display: 'flex', gap: '10px', marginTop: '6px' }}>
                                                    <button
                                                        onClick={() => {
                                                            const reset = { city: 'ALL', branch: 'ALL' };
                                                            setStagedFilters(reset);
                                                            setFilterCity('ALL'); setFilterBranch('ALL');
                                                            setShowFilterPopover(false);
                                                        }}
                                                        style={{ ...s.btnGray, flex: 1, padding: '8px' }}
                                                    >
                                                        Clear
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setFilterCity(stagedFilters.city);
                                                            setFilterBranch(stagedFilters.branch);
                                                            setShowFilterPopover(false);
                                                        }}
                                                        style={{ ...s.btnBlue, flex: 1, padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                                                    >
                                                        <Check size={16} /> Apply
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>

                        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '16px' }}>
                            {/* Period label for non-custom */}
                            {activePeriod !== 'custom' && (
                                <div style={{ fontSize: '13px', color: '#1e293b', fontWeight: '700', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '8px', background: '#f8fafc', padding: '8px 16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                    <Calendar size={16} style={{ color: '#3b82f6' }} />
                                    {periodLabel}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Error */}
                    {error && (
                        <div style={{ background: '#fff1f2', border: '1px solid #fda4af', color: '#9f1239', padding: '12px 16px', borderRadius: '8px', fontSize: '13px', marginBottom: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <AlertCircle size={18} />
                            {error}
                        </div>
                    )}

                    {/* Loading */}
                    {loading && (
                        <div style={{ textAlign: 'center', padding: '80px', color: '#94a3b8', fontSize: '14px' }}>
                            <Loader2 size={40} style={{ animation: 'spin 1.5s linear infinite', margin: '0 auto 12px' }} />
                            <div style={{ fontWeight: '600' }}>Fetching feedback data…</div>
                        </div>
                    )}

                    {!loading && allData.length > 0 && (
                        activeView === 'dashboard' ? (
                            <>
                                {/* ── KPI Cards ── */}
                                <div style={s.kpiGrid}>
                                    {/* NPS Score Card — centered gauge */}
                                    <div className="kpi-card" style={{ background: '#fff', borderRadius: '12px', padding: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0', gridColumn: 'span 1', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
                                        <NPSGauge score={npsScore} />
                                    </div>
                                    {KPI_CARDS.map((k, i) => (
                                        <div key={i} className="kpi-card" style={{ ...s.kpiCard, background: k.bg, border: `1px solid ${k.border}` }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                <div style={{ color: k.color, opacity: 0.9 }}>{k.icon}</div>
                                                <div style={{ background: 'rgba(255,255,255,0.7)', borderRadius: '14px', padding: '2px 6px', fontSize: '9px', fontWeight: '800', color: k.color, letterSpacing: '0.4px' }}>
                                                    NPS Score
                                                </div>
                                            </div>
                                            <div style={{ marginTop: '2px' }}>
                                                <div style={{ fontSize: '22px', fontWeight: '800', lineHeight: 1.1, color: k.color }}>{k.num}</div>
                                                <div style={{ fontSize: '11px', color: k.color, opacity: 0.85, fontWeight: '700', marginTop: '2px' }}>{k.lbl}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* ── Rating Breakdown ── */}
                                <div style={s.chartRow}>
                                    {CATEGORIES.map(cat => (
                                        <RatingBreakdown key={cat.key} data={filtered} categoryKey={cat.key} label={cat.label} icon={cat.icon} />
                                    ))}
                                </div>

                                {/* ── Filters / Search ── */}
                                <div style={s.filters}>
                                    <div>
                                        <label style={s.label}>Search Results</label>
                                        <input
                                            style={{ ...s.input, width: '300px' }}
                                            placeholder="Name, Bill No, RefDoc, Mobile…"
                                            value={search}
                                            onChange={e => setSearch(e.target.value)}
                                        />
                                    </div>
                                    <div style={{ marginLeft: 'auto', fontSize: '13px', color: '#64748b', alignSelf: 'center', fontWeight: '600' }}>
                                        Showing&nbsp;<strong style={{ color: '#1e293b' }}>{filtered.length}</strong>&nbsp;of&nbsp;<strong style={{ color: '#1e293b' }}>{allData.length}</strong>&nbsp;responses
                                    </div>
                                </div>

                                {/* ── Table ── */}
                                <div style={s.tbl}>
                                    <div style={{ overflowX: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
                                            <thead>
                                                <tr>
                                                    {['#', 'Ref Doc ID', 'Bill No', 'Business Date', 'City', 'Branch', 'Customer', 'Sale Items', 'Ambience', 'Cleanliness', 'Availability', 'Staff', 'Recommend'].map(h => (
                                                        <th key={h} style={s.th}>{h}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paged.map((r, i) => (
                                                    <tr key={r.UniqueID || i}
                                                        style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc', transition: 'background 0.15s' }}
                                                        onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                                                        onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafbfc'}
                                                    >
                                                        <td style={{ ...s.td, color: '#94a3b8', fontSize: '11px' }}>{(page - 1) * PAGE_SIZE + i + 1}</td>
                                                        <td style={{ ...s.td, fontWeight: '700', color: '#1d4ed8' }}>{r.RefDocID || '-'}</td>
                                                        <td style={{ ...s.td, fontWeight: '600', color: '#1e293b' }}>{r.BillNo || '-'}</td>
                                                        <td style={s.td}>{formatDate(r.BusinessDate)}</td>
                                                        <td style={s.td}>{r.AreaName || '-'}</td>
                                                        <td style={{ ...s.td, fontWeight: '700' }}>{r.BranchName || '-'}</td>
                                                        <td style={s.td}>
                                                            <div style={{ fontWeight: '700', color: '#1e293b' }}>{r.UserName || '-'}</div>
                                                            <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px', fontWeight: '500' }}>{r.Mobile || ''}</div>
                                                        </td>
                                                        <td style={{ ...s.td, maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', color: '#64748b' }} title={r.SaleItems}>{r.SaleItems || '-'}</td>
                                                        <td style={s.td}><RatingBadge value={r.AMBIENCE} /></td>
                                                        <td style={s.td}><RatingBadge value={r.CLEANLY} /></td>
                                                        <td style={s.td}><RatingBadge value={r.AVAILABLE} /></td>
                                                        <td style={s.td}><RatingBadge value={r.STAFF} /></td>
                                                        <td style={s.td}><RatingBadge value={r.RECOMMEND} /></td>
                                                    </tr>
                                                ))}
                                                {paged.length === 0 && (
                                                    <tr>
                                                        <td colSpan={13} style={{ ...s.td, textAlign: 'center', color: '#94a3b8', padding: '60px', fontSize: '14px' }}>
                                                            <Search size={40} style={{ opacity: 0.2, margin: '0 auto 12px' }} />
                                                            No records found for this period
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderTop: '1px solid #f1f5f9', background: '#f8fafc' }}>
                                        <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>
                                            Page <strong style={{ color: '#1e293b' }}>{page}</strong> of <strong style={{ color: '#1e293b' }}>{totalPages}</strong>
                                        </span>
                                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                            <button style={s.btnGray} disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                                                <ChevronLeft size={16} />
                                            </button>
                                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                                                return p <= totalPages ? (
                                                    <button key={p} onClick={() => setPage(p)}
                                                        style={{ ...s.btnGray, minWidth: '34px', padding: '6px', background: p === page ? '#2563eb' : '#fff', color: p === page ? '#fff' : '#1e293b', border: '1px solid #e2e8f0', fontWeight: p === page ? '800' : '600' }}
                                                    >{p}</button>
                                                ) : null;
                                            })}
                                            <button style={s.btnGray} disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                                                <ChevronRight size={16} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                {/* ── Comparison Grid Row 1 (Top/Bottom Stores Leaderboards) ── */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '20px', marginBottom: '20px' }}>
                                    {/* Top 10 Stores */}
                                    <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                                        <h3 style={{ fontSize: '15px', fontWeight: '800', color: '#1e293b', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px' }}>
                                            <TrendingUp size={18} style={{ color: '#10b981' }} />
                                            Top 10 Stores (High NPS)
                                        </h3>
                                        <div style={{ overflowY: 'auto', maxHeight: '420px', flex: 1 }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                <thead>
                                                    <tr>
                                                        <th style={{ ...s.th, padding: '10px 8px', fontSize: '10px' }}>Rank</th>
                                                        <th style={{ ...s.th, padding: '10px 8px', fontSize: '10px' }}>Store Name</th>
                                                        <th style={{ ...s.th, padding: '10px 8px', fontSize: '10px' }}>City</th>
                                                        <th style={{ ...s.th, padding: '10px 8px', fontSize: '10px', textAlign: 'right' }}>Responses</th>
                                                        <th style={{ ...s.th, padding: '10px 8px', fontSize: '10px', textAlign: 'center' }}>NPS Score</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {top10Stores.map((item, index) => (
                                                        <tr key={item.store} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                            <td style={{ ...s.td, padding: '10px 8px', fontWeight: 'bold', color: index === 0 ? '#d97706' : index === 1 ? '#4b5563' : index === 2 ? '#b45309' : '#64748b' }}>
                                                                #{index + 1}
                                                            </td>
                                                            <td style={{ ...s.td, padding: '10px 8px', fontWeight: '700' }}>{item.store}</td>
                                                            <td style={{ ...s.td, padding: '10px 8px', color: '#64748b' }}>{item.city}</td>
                                                            <td style={{ ...s.td, padding: '10px 8px', textAlign: 'right', fontWeight: '600' }}>{item.total}</td>
                                                            <td style={{ ...s.td, padding: '10px 8px', textAlign: 'center' }}><NPSScoreBadge score={item.nps} /></td>
                                                        </tr>
                                                    ))}
                                                    {top10Stores.length === 0 && (
                                                        <tr>
                                                            <td colSpan={5} style={{ ...s.td, textAlign: 'center', color: '#94a3b8', padding: '20px' }}>No data available</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    {/* Bottom 10 Stores */}
                                    <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                                        <h3 style={{ fontSize: '15px', fontWeight: '800', color: '#1e293b', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px' }}>
                                            <TrendingUp size={18} style={{ color: '#ef4444', transform: 'rotate(180deg)' }} />
                                            Bottom 10 Stores (Low NPS)
                                        </h3>
                                        <div style={{ overflowY: 'auto', maxHeight: '420px', flex: 1 }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                <thead>
                                                    <tr>
                                                        <th style={{ ...s.th, padding: '10px 8px', fontSize: '10px' }}>Rank</th>
                                                        <th style={{ ...s.th, padding: '10px 8px', fontSize: '10px' }}>Store Name</th>
                                                        <th style={{ ...s.th, padding: '10px 8px', fontSize: '10px' }}>City</th>
                                                        <th style={{ ...s.th, padding: '10px 8px', fontSize: '10px', textAlign: 'right' }}>Responses</th>
                                                        <th style={{ ...s.th, padding: '10px 8px', fontSize: '10px', textAlign: 'center' }}>NPS Score</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {bottom10Stores.map((item, index) => (
                                                        <tr key={item.store} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                            <td style={{ ...s.td, padding: '10px 8px', fontWeight: 'bold', color: '#64748b' }}>
                                                                #{index + 1}
                                                            </td>
                                                            <td style={{ ...s.td, padding: '10px 8px', fontWeight: '700' }}>{item.store}</td>
                                                            <td style={{ ...s.td, padding: '10px 8px', color: '#64748b' }}>{item.city}</td>
                                                            <td style={{ ...s.td, padding: '10px 8px', textAlign: 'right', fontWeight: '600' }}>{item.total}</td>
                                                            <td style={{ ...s.td, padding: '10px 8px', textAlign: 'center' }}><NPSScoreBadge score={item.nps} /></td>
                                                        </tr>
                                                    ))}
                                                    {bottom10Stores.length === 0 && (
                                                        <tr>
                                                            <td colSpan={5} style={{ ...s.td, textAlign: 'center', color: '#94a3b8', padding: '20px' }}>No data available</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>

                                {/* ── Comparison Grid Row 2 (Citywise Cumulative NPS) ── */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '20px', marginBottom: '20px' }}>
                                    {/* Cumulative Citywise NPS */}
                                    <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                                        <h3 style={{ fontSize: '15px', fontWeight: '800', color: '#1e293b', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px' }}>
                                            <Store size={18} style={{ color: '#2563eb' }} />
                                            Cumulative Citywise NPS
                                        </h3>
                                        <div style={{ overflowY: 'auto', maxHeight: '350px', flex: 1 }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                <thead>
                                                    <tr>
                                                        <th style={{ ...s.th, padding: '10px 8px', fontSize: '10px' }}>Rank</th>
                                                        <th style={{ ...s.th, padding: '10px 8px', fontSize: '10px' }}>City</th>
                                                        <th style={{ ...s.th, padding: '10px 8px', fontSize: '10px', textAlign: 'right' }}>Responses</th>
                                                        <th style={{ ...s.th, padding: '10px 8px', fontSize: '10px', textAlign: 'center' }}>NPS Score</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {citywiseNPS.map((item, index) => (
                                                        <tr key={item.city} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                            <td style={{ ...s.td, padding: '10px 8px', fontWeight: 'bold', color: '#64748b' }}>
                                                                #{index + 1}
                                                            </td>
                                                            <td style={{ ...s.td, padding: '10px 8px', fontWeight: '700' }}>{item.city}</td>
                                                            <td style={{ ...s.td, padding: '10px 8px', textAlign: 'right', fontWeight: '600' }}>{item.total}</td>
                                                            <td style={{ ...s.td, padding: '10px 8px', textAlign: 'center' }}><NPSScoreBadge score={item.nps} /></td>
                                                        </tr>
                                                    ))}
                                                    {citywiseNPS.length === 0 && (
                                                        <tr>
                                                            <td colSpan={4} style={{ ...s.td, textAlign: 'center', color: '#94a3b8', padding: '20px' }}>No data available</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    {/* Month-on-Month Citywise NPS */}
                                    <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                                        <h3 style={{ fontSize: '15px', fontWeight: '800', color: '#1e293b', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px' }}>
                                            <CalendarRange size={18} style={{ color: '#2563eb' }} />
                                            Month-on-Month Citywise NPS
                                        </h3>
                                        <div style={{ overflowY: 'auto', overflowX: 'auto', maxHeight: '350px', flex: 1 }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '400px' }}>
                                                <thead>
                                                    <tr>
                                                        <th style={s.th}>City</th>
                                                        {momCityNPSData.months.map(m => (
                                                            <th key={m.sortKey} style={{ ...s.th, textAlign: 'center', fontSize: '10px', padding: '10px 6px' }}>{m.label}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {momCityNPSData.cities.map((item) => (
                                                        <tr key={item.city} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                            <td style={{ ...s.td, padding: '10px 8px', fontWeight: '700' }}>{item.city}</td>
                                                            {momCityNPSData.months.map(m => {
                                                                const scoreData = item.momScores[m.sortKey];
                                                                const hasData = scoreData && scoreData.nps !== null;
                                                                return (
                                                                    <td key={m.sortKey} style={{ ...s.td, padding: '10px 6px', textAlign: 'center' }}>
                                                                        {hasData ? (
                                                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                                                <span style={{ fontWeight: '700', color: scoreData.nps > 30 ? '#10b981' : scoreData.nps > 0 ? '#f59e0b' : '#ef4444' }}>
                                                                                    {scoreData.nps}
                                                                                </span>
                                                                                <span style={{ fontSize: '9px', color: '#94a3b8', fontWeight: '600' }}>
                                                                                    ({scoreData.total})
                                                                                </span>
                                                                            </div>
                                                                        ) : (
                                                                            <span style={{ color: '#cbd5e1' }}>-</span>
                                                                        )}
                                                                    </td>
                                                                );
                                                            })}
                                                        </tr>
                                                    ))}
                                                    {momCityNPSData.cities.length === 0 && (
                                                        <tr>
                                                            <td colSpan={momCityNPSData.months.length + 1} style={{ ...s.td, textAlign: 'center', color: '#94a3b8', padding: '20px' }}>
                                                                No data available
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>

                                {/* ── Comparison Grid Detractor Analysis Panel ── */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px', marginBottom: '20px' }}>
                                    
                                    {/* City with most detractors */}
                                    <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                                        <h3 style={{ fontSize: '15px', fontWeight: '800', color: '#1e293b', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px' }}>
                                            <MapPin size={18} style={{ color: '#ef4444' }} />
                                            City with Most Detractors
                                        </h3>
                                        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: 1, padding: '10px 0' }}>
                                            <span style={{ fontSize: '11px', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Worst Performing Area</span>
                                            <span style={{ fontSize: '28px', fontWeight: '900', color: '#b91c1c', margin: '4px 0 8px' }}>
                                                {detractorInsights.worstCity.name}
                                            </span>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '8px', padding: '10px 14px' }}>
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <span style={{ fontSize: '18px', fontWeight: '800', color: '#991b1b' }}>{detractorInsights.worstCity.count}</span>
                                                    <span style={{ fontSize: '10px', color: '#b91c1c', fontWeight: '600' }}>Detractors</span>
                                                </div>
                                                <div style={{ width: '1px', height: '28px', background: '#fca5a5' }}></div>
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <span style={{ fontSize: '18px', fontWeight: '800', color: '#991b1b' }}>{detractorInsights.worstCity.rate}%</span>
                                                    <span style={{ fontSize: '10px', color: '#b91c1c', fontWeight: '600' }}>Detractor Rate</span>
                                                </div>
                                                <div style={{ width: '1px', height: '28px', background: '#fca5a5' }}></div>
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <span style={{ fontSize: '18px', fontWeight: '800', color: '#991b1b' }}>{detractorInsights.worstCity.total}</span>
                                                    <span style={{ fontSize: '10px', color: '#b91c1c', fontWeight: '600' }}>Total Responses</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Shop with most detractors */}
                                    <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                                        <h3 style={{ fontSize: '15px', fontWeight: '800', color: '#1e293b', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px' }}>
                                            <Store size={18} style={{ color: '#ef4444' }} />
                                            Store with Most Detractors
                                        </h3>
                                        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: 1, padding: '10px 0' }}>
                                            <span style={{ fontSize: '11px', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Worst Performing Store</span>
                                            <span style={{ fontSize: '28px', fontWeight: '900', color: '#b91c1c', margin: '4px 0 2px' }}>
                                                {detractorInsights.worstShop.name}
                                            </span>
                                            <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '8px' }}>
                                                City/Area: {detractorInsights.worstShop.city}
                                            </span>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '8px', padding: '10px 14px' }}>
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <span style={{ fontSize: '18px', fontWeight: '800', color: '#991b1b' }}>{detractorInsights.worstShop.count}</span>
                                                    <span style={{ fontSize: '10px', color: '#b91c1c', fontWeight: '600' }}>Detractors</span>
                                                </div>
                                                <div style={{ width: '1px', height: '28px', background: '#fca5a5' }}></div>
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <span style={{ fontSize: '18px', fontWeight: '800', color: '#991b1b' }}>{detractorInsights.worstShop.rate}%</span>
                                                    <span style={{ fontSize: '10px', color: '#b91c1c', fontWeight: '600' }}>Detractor Rate</span>
                                                </div>
                                                <div style={{ width: '1px', height: '28px', background: '#fca5a5' }}></div>
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <span style={{ fontSize: '18px', fontWeight: '800', color: '#991b1b' }}>{detractorInsights.worstShop.total}</span>
                                                    <span style={{ fontSize: '10px', color: '#b91c1c', fontWeight: '600' }}>Total Responses</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Question with most detractors */}
                                    <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                                        <h3 style={{ fontSize: '15px', fontWeight: '800', color: '#1e293b', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px' }}>
                                            <ThumbsDown size={18} style={{ color: '#ef4444' }} />
                                            Most Detracted Category
                                        </h3>
                                        <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <span style={{ fontSize: '11px', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Worst Rated Question</span>
                                                    <span style={{ fontSize: '18px', fontWeight: '800', color: '#b91c1c', marginTop: '2px' }}>
                                                        {detractorInsights.worstQuestion.label}
                                                    </span>
                                                </div>
                                                <span style={{ background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '8px', padding: '4px 10px', textAlign: 'right', display: 'flex', flexDirection: 'column' }}>
                                                    <span style={{ fontSize: '14px', fontWeight: '800', color: '#b91c1c' }}>{detractorInsights.worstQuestion.count} detractors</span>
                                                    <span style={{ fontSize: '9px', color: '#ef4444', fontWeight: '700' }}>({detractorInsights.worstQuestion.rate}% rate)</span>
                                                </span>
                                            </div>
                                            
                                            {/* Ranked Categories List */}
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, justifyContent: 'center' }}>
                                                {detractorInsights.allQuestions.map((cat, idx) => (
                                                    <div key={cat.label} style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', fontWeight: '700', color: idx === 0 ? '#b91c1c' : '#475569' }}>
                                                            <span>{cat.label}</span>
                                                            <span>{cat.count} ({cat.rate}%)</span>
                                                        </div>
                                                        <div style={{ height: '4px', background: '#f1f5f9', borderRadius: '2px', overflow: 'hidden' }}>
                                                            <div style={{ height: '100%', width: `${cat.rate}%`, background: idx === 0 ? '#ef4444' : '#f87171', borderRadius: '2px' }}></div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* ── Comparison Grid Detractor List Table ── */}
                                <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px', borderBottom: '1px solid #f1f5f9', paddingBottom: '14px' }}>
                                        <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <ClipboardX size={20} style={{ color: '#ef4444' }} />
                                            List of All Detractor Feedbacks
                                        </h3>
                                        <button 
                                            onClick={handleDownloadCSV}
                                            style={{
                                                ...s.btnBlue,
                                                padding: '8px 16px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                fontSize: '13px',
                                                background: '#ef4444',
                                                borderColor: '#ef4444',
                                                color: '#fff'
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.background = '#dc2626'}
                                            onMouseLeave={e => e.currentTarget.style.background = '#ef4444'}
                                        >
                                            <FileDown size={16} /> Download CSV Report
                                        </button>
                                    </div>
                                    <div style={{ overflowX: 'auto', maxHeight: '450px' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '950px' }}>
                                            <thead>
                                                <tr>
                                                    {['#', 'Bill No', 'Customer Name', 'Customer Number', 'Shop', 'City', 'Item Name', 'Detracted Categories'].map(h => (
                                                        <th key={h} style={s.th}>{h}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {detractorList.map((item, idx) => (
                                                    <tr key={idx} style={{ background: idx % 2 === 0 ? '#fff' : '#fafbfc', borderBottom: '1px solid #f1f5f9' }}>
                                                        <td style={{ ...s.td, color: '#94a3b8', fontSize: '11px' }}>{idx + 1}</td>
                                                        <td style={{ ...s.td, fontWeight: '600' }}>{item.BillNo || '-'}</td>
                                                        <td style={{ ...s.td, fontWeight: '700' }}>{item.UserName || '-'}</td>
                                                        <td style={s.td}>{item.Mobile || '-'}</td>
                                                        <td style={{ ...s.td, fontWeight: '700' }}>{item.BranchName || '-'}</td>
                                                        <td style={s.td}>{item.AreaName || '-'}</td>
                                                        <td style={{ ...s.td, maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', color: '#64748b' }} title={item.SaleItems}>
                                                            {item.SaleItems || '-'}
                                                        </td>
                                                        <td style={s.td}>
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                                {item.detractedQuestions.map(q => (
                                                                    <span key={q} style={{ background: '#fef2f2', border: '1px solid #fee2e2', color: '#b91c1c', fontSize: '10px', padding: '2px 8px', borderRadius: '4px', fontWeight: '700' }}>
                                                                        {q}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {detractorList.length === 0 && (
                                                    <tr>
                                                        <td colSpan={8} style={{ ...s.td, textAlign: 'center', color: '#94a3b8', padding: '20px' }}>
                                                            No detractor records found for this period
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* ── Comparison Grid Row 3 (Month on Month NPS Store Drill-Down Dashboard) ── */}
                                <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px', borderBottom: '1px solid #f1f5f9', paddingBottom: '14px' }}>
                                        <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <CalendarRange size={20} style={{ color: '#2563eb' }} />
                                            Month-on-Month Store Drill-Down Dashboard
                                        </h3>
                                        
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <label style={{ fontSize: '13px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Store:</label>
                                            <select 
                                                value={selectedDrillDownStore} 
                                                onChange={e => setSelectedDrillDownStore(e.target.value)}
                                                style={{
                                                    padding: '8px 16px',
                                                    borderRadius: '8px',
                                                    border: '1px solid #cbd5e1',
                                                    background: '#fff',
                                                    fontSize: '13px',
                                                    fontWeight: '700',
                                                    color: '#1e293b',
                                                    cursor: 'pointer',
                                                    outline: 'none',
                                                    transition: 'border-color 0.2s',
                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                                }}
                                                onFocus={e => e.target.style.borderColor = '#2563eb'}
                                                onBlur={e => e.target.style.borderColor = '#cbd5e1'}
                                            >
                                                {momNPSData.branches.map(b => (
                                                    <option key={b.branch} value={b.branch}>{b.branch}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    {selectedStoreDrillDownData.length === 0 ? (
                                        <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontSize: '14px', border: '1px dashed #cbd5e1', borderRadius: '8px' }}>
                                            No monthly trend data available for this store in the selected date range.
                                        </div>
                                    ) : (
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '20px' }}>
                                            {selectedStoreDrillDownData.map((month) => {
                                                // Determine change badge color and symbol
                                                let changeBg = '#f1f5f9';
                                                let changeColor = '#475569';
                                                if (month.changeType === 'improvement') {
                                                    changeBg = '#dcfce7';
                                                    changeColor = '#15803d';
                                                } else if (month.changeType === 'decline') {
                                                    changeBg = '#fee2e2';
                                                    changeColor = '#b91c1c';
                                                }

                                                // Rating Warning Helper
                                                const renderRatingRow = (label, val) => {
                                                    const isLow = val !== '-' && parseFloat(val) < 4.0;
                                                    return (
                                                        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', padding: '6px 0', borderBottom: '1px solid #f8fafc' }}>
                                                            <span style={{ color: '#64748b', fontWeight: '500' }}>{label}</span>
                                                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '700', color: isLow ? '#d97706' : '#1e293b' }}>
                                                                {val} {isLow && <span style={{ color: '#ea580c', fontWeight: 'bold' }} title="Rating below 4.0">⚠</span>}
                                                            </span>
                                                        </div>
                                                    );
                                                };

                                                return (
                                                    <div 
                                                        key={month.sortKey} 
                                                        style={{ 
                                                            background: '#f8fafc', 
                                                            borderRadius: '12px', 
                                                            padding: '20px', 
                                                            border: '1px solid #e2e8f0', 
                                                            boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                                                            display: 'flex', 
                                                            flexDirection: 'column',
                                                            transition: 'transform 0.2s, box-shadow 0.2s',
                                                            cursor: 'default'
                                                        }}
                                                        onMouseEnter={e => {
                                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.05)';
                                                            e.currentTarget.style.borderColor = '#cbd5e1';
                                                        }}
                                                        onMouseLeave={e => {
                                                            e.currentTarget.style.transform = 'none';
                                                            e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.02)';
                                                            e.currentTarget.style.borderColor = '#e2e8f0';
                                                        }}
                                                    >
                                                        {/* Card Header */}
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                                                            <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '800', color: '#0f172a' }}>
                                                                {month.monthLabel}
                                                            </h4>
                                                            <span style={{ 
                                                                fontSize: '11px', 
                                                                fontWeight: '700', 
                                                                padding: '3px 8px', 
                                                                borderRadius: '20px', 
                                                                background: changeBg, 
                                                                color: changeColor 
                                                            }}>
                                                                {month.changeText}
                                                            </span>
                                                        </div>

                                                        {/* Main Metrics (NPS & Responses) */}
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px', background: '#fff', padding: '12px 16px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                                                            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                                                <span style={{ fontSize: '10px', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Current NPS</span>
                                                                <span style={{ fontSize: '32px', fontWeight: '900', color: month.nps > 30 ? '#10b981' : month.nps > 0 ? '#f59e0b' : '#ef4444', lineHeight: 1.1 }}>{month.nps}</span>
                                                            </div>
                                                            <div style={{ width: '1px', height: '40px', background: '#e2e8f0' }}></div>
                                                            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                                                <span style={{ fontSize: '10px', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Responses</span>
                                                                <span style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b', lineHeight: 1.1 }}>{month.total}</span>
                                                            </div>
                                                        </div>

                                                        {/* Promoters / Passives / Detractors breakdown */}
                                                        <div style={{ marginBottom: '16px' }}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', marginBottom: '6px' }}>
                                                                <span>Promoters: <strong style={{ color: '#10b981' }}>{month.promoters}</strong></span>
                                                                <span>Passives: <strong style={{ color: '#64748b' }}>{month.passives}</strong></span>
                                                                <span>Detractors: <strong style={{ color: '#ef4444' }}>{month.detractors}</strong></span>
                                                            </div>
                                                            {/* Horizontal breakdown bar */}
                                                            {month.total > 0 && (
                                                                <div style={{ display: 'flex', height: '6px', borderRadius: '3px', overflow: 'hidden', background: '#e2e8f0' }}>
                                                                    <div style={{ width: `${(month.promoters / month.total) * 100}%`, background: '#10b981' }} title="Promoters"></div>
                                                                    <div style={{ width: `${(month.passives / month.total) * 100}%`, background: '#94a3b8' }} title="Passives"></div>
                                                                    <div style={{ width: `${(month.detractors / month.total) * 100}%`, background: '#ef4444' }} title="Detractors"></div>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Ratings Breakdown */}
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', background: '#fff', borderRadius: '10px', padding: '12px 14px', border: '1px solid #e2e8f0' }}>
                                                            <div style={{ fontSize: '10px', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px', borderBottom: '1px solid #f1f5f9', paddingBottom: '4px' }}>Rating Breakdown</div>
                                                            {renderRatingRow('Ambience', month.ratings.ambience)}
                                                            {renderRatingRow('Cleanliness', month.ratings.cleanliness)}
                                                            {renderRatingRow('Availability', month.ratings.availability)}
                                                            {renderRatingRow('Staff', month.ratings.staff)}
                                                            {renderRatingRow('Recommend', month.ratings.recommend)}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {/* ── Comparison Grid Row 4 (Month on Month NPS Score of Each Store) ── */}
                                <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px', borderBottom: '1px solid #f1f5f9', paddingBottom: '12px' }}>
                                        <h3 style={{ fontSize: '15px', fontWeight: '800', color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <CalendarRange size={18} style={{ color: '#2563eb' }} />
                                            Month on Month NPS Score of Each Store
                                        </h3>
                                        <div style={{ position: 'relative' }}>
                                            <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                                            <input 
                                                style={{ ...s.input, width: '250px', padding: '6px 12px 6px 30px', fontSize: '12px' }}
                                                placeholder="Filter store name..."
                                                value={comparisonSearch}
                                                onChange={e => setComparisonSearch(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    
                                    <div style={{ overflowX: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                                            <thead>
                                                <tr>
                                                    <th style={s.th}>Store / Branch</th>
                                                    <th style={s.th}>City</th>
                                                    {momNPSData.months.length === 0 ? (
                                                        <th style={{ ...s.th, textAlign: 'center' }}>No months in period</th>
                                                    ) : (
                                                        momNPSData.months.map(m => (
                                                            <th key={m.sortKey} style={{ ...s.th, textAlign: 'center' }}>{m.label}</th>
                                                        ))
                                                    )}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredBranchesMoM.map((b, idx) => (
                                                    <tr key={b.branch} style={{ background: idx % 2 === 0 ? '#fff' : '#fafbfc', transition: 'background 0.15s' }}
                                                        onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                                                        onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#fafbfc'}
                                                    >
                                                        <td style={{ ...s.td, fontWeight: '700' }}>{b.branch}</td>
                                                        <td style={{ ...s.td, color: '#64748b' }}>{b.city}</td>
                                                        {momNPSData.months.map(m => {
                                                            const scoreData = b.momScores[m.sortKey];
                                                            const score = scoreData ? scoreData.nps : null;
                                                            const reps = scoreData ? scoreData.total : 0;
                                                            return (
                                                                <td key={m.sortKey} style={{ ...s.td, textAlign: 'center' }}>
                                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                                                        <NPSScoreBadge score={score} />
                                                                        {score !== null && (
                                                                            <span style={{ fontSize: '9px', color: '#94a3b8', fontWeight: '500' }}>{reps} reps</span>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            );
                                                        })}
                                                    </tr>
                                                ))}
                                                {filteredBranchesMoM.length === 0 && (
                                                    <tr>
                                                        <td colSpan={momNPSData.months.length + 2} style={{ ...s.td, textAlign: 'center', color: '#94a3b8', padding: '40px' }}>
                                                            No store matches your search
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </>
                        )
                    )}

                    {!loading && allData.length === 0 && !error && (
                        <div style={{ textAlign: 'center', padding: '100px 20px', color: '#94a3b8' }}>
                            <ClipboardX size={60} style={{ opacity: 0.15, margin: '0 auto 16px' }} />
                            <div style={{ fontSize: '16px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>No feedback data available</div>
                            <div style={{ fontSize: '13px' }}>Try refreshing or check your connection</div>
                        </div>
                    )}

                </div>
            </div>
        </>
    );
};

export default FeedbackDashboard;
